import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import i18n from "i18next";
import {
  LuPlus,
  LuMinus,
  LuCopy,
  LuEye,
  LuMaximize,
  LuSettings2,
  LuTrash2,
  LuChevronRight,
} from "react-icons/lu";
import { getAssessmentMenu } from "./assessmentMenuConfig";
import "./_assessmentMenu.scss";

// Gamma-style over-box menu for assessment plugin boxes: a collapsed pill of
// the most-used actions plus a gear-expanded popover whose rows are driven by
// the per-type descriptor in assessmentMenuConfig.js. Rendered (and gated) by
// EditorShortcuts; portals into #editorBoxIconsRoot like the legacy toolbar.

// Number field with a local draft: in-range values commit live, out-of-range
// ones (e.g. min length above max length) never reach state — they are clamped
// on blur/Enter. Typing "15" when the floor is 10 must not snap "1" to "10".
function NumberInput({ value, min, max, onCommit, ariaLabelledBy }) {
  const toStr = (v) => (v == null ? "" : String(v));
  const [draft, setDraft] = useState(toStr(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(toStr(value));
  }, [value]);

  const inRange = (n) => !isNaN(n) && (min == null || n >= min) && (max == null || n <= max);

  const finish = () => {
    focused.current = false;
    let n = draft === "" ? NaN : Number(draft);
    if (isNaN(n)) n = typeof value === "number" ? value : (min ?? 0);
    if (min != null && n < min) n = min;
    if (max != null && n > max) n = max;
    setDraft(toStr(n));
    if (n !== value) onCommit(n);
  };

  return (
    <input
      type="number"
      className="am-num"
      aria-labelledby={ariaLabelledBy}
      min={min}
      max={max}
      value={draft}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        const n = e.target.value === "" ? NaN : Number(e.target.value);
        if (inRange(n) && n !== value) onCommit(n);
      }}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.target.blur();
        }
      }}
    />
  );
}

function Row({ row, state, setKey, onAddAnswer, onRemoveAnswer, openSettings, toolbarId }) {
  const value = state[row.key];
  const label = i18n.t(row.label);

  if (row.control === "stepper") {
    const n = typeof value === "number" ? value : Number(value) || 0;
    return (
      <div className="am-row">
        <span className="am-lbl">{label}</span>
        <span className="am-stepper">
          <button
            type="button"
            aria-label={i18n.t("AssessmentMenu.removeOne")}
            disabled={n <= 1}
            onClick={onRemoveAnswer}
          >
            −
          </button>
          <b>{n}</b>
          <button type="button" aria-label={i18n.t("AssessmentMenu.addOne")} onClick={onAddAnswer}>
            +
          </button>
        </span>
      </div>
    );
  }

  if (row.control === "segmented") {
    return (
      <div className="am-row">
        <span className="am-lbl">{label}</span>
        <span className="am-seg" role="radiogroup" aria-label={label}>
          {row.options.map((opt) => (
            <span
              key={opt.value}
              role="radio"
              tabIndex={0}
              aria-checked={value === opt.value}
              onClick={() => setKey(row.key, opt.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setKey(row.key, opt.value);
                }
              }}
            >
              {opt.label}
            </span>
          ))}
        </span>
      </div>
    );
  }

  if (row.control === "toggle") {
    const on = !!value;
    return (
      <div className="am-row">
        <span className="am-lbl">{label}</span>
        <button
          type="button"
          className="am-toggle"
          role="switch"
          aria-checked={on}
          aria-label={label}
          onClick={() => setKey(row.key, !on)}
        />
      </div>
    );
  }

  if (row.control === "number") {
    // Effective bounds: the row's static range, tightened by a sibling key
    // (e.g. minLength ≤ maxLength).
    const sib = (k) => (k && typeof state[k] === "number" ? state[k] : null);
    let min = row.min;
    let max = row.max;
    if (sib(row.minKey) != null) min = Math.max(min ?? -Infinity, sib(row.minKey));
    if (sib(row.maxKey) != null) max = Math.min(max ?? Infinity, sib(row.maxKey));
    return (
      <div className="am-row">
        <span className="am-lbl" id={`am-num-${row.key}`}>
          {label}
        </span>
        <NumberInput
          ariaLabelledBy={`am-num-${row.key}`}
          min={min}
          max={max}
          value={value}
          onCommit={(n) => setKey(row.key, n)}
        />
      </div>
    );
  }

  // control === "buttons": which secondary action the learner sees after
  // answering — an inline segmented picker (no modal). Writes state.buttons.
  // Per-button label texts stay in the full settings modal.
  const buttonOpts = [
    { value: "tryAgain", label: i18n.t("MultipleChoice.tryAgain") },
    { value: "seeAnswer", label: i18n.t("MultipleChoice.seeAnswer") },
  ];
  return (
    <div className="am-row">
      <span className="am-lbl">{label}</span>
      <span className="am-seg" role="radiogroup" aria-label={label}>
        {buttonOpts.map((opt) => (
          <span
            key={opt.value}
            role="radio"
            tabIndex={0}
            aria-checked={value === opt.value}
            onClick={() => setKey("buttons", opt.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setKey("buttons", opt.value);
              }
            }}
          >
            {opt.label}
          </span>
        ))}
      </span>
    </div>
  );
}

export default function AssessmentBoxMenu(props) {
  const { box, toolbar, left, top } = props;
  const menu = getAssessmentMenu(toolbar && toolbar.pluginId);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDocDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (!menu || !box || !toolbar) return null;

  const state = toolbar.state || {};
  const setKey = (key, value) => props.onToolbarUpdated(box.id, "main", "state", key, value);
  const stop = (e) => e.stopPropagation();

  const pill = (
    <div
      ref={rootRef}
      className="assessmentBoxMenu am-pill"
      role="toolbar"
      aria-label={i18n.t("AssessmentMenu.assessmentOptions")}
      // `left`/`top` from EditorShortcuts.resize() anchor the box's top-right
      // corner (the toolbar width isn't measured for this component), so
      // right-align the pill to that anchor to sit over the question.
      // `--pill-scale` (set on #topPillRoot by EditorCanvasSli) shrinks the pill
      // with the stage; the bottom-right origin keeps it hugging the box corner.
      style={{
        position: "absolute",
        left,
        top,
        transform: "translateX(-100%) scale(var(--pill-scale, 1))",
        transformOrigin: "100% 100%",
      }}
      onMouseDown={stop}
      onClick={stop}
    >
      {menu.showAddRemove && (
        <>
          <button
            type="button"
            className="am-ic"
            aria-label={i18n.t("AssessmentMenu.addAnswer")}
            title={i18n.t("AssessmentMenu.addAnswer")}
            onClick={props.onAddAnswer}
          >
            <LuPlus />
          </button>
          <button
            type="button"
            className="am-ic"
            aria-label={i18n.t("AssessmentMenu.removeAnswer")}
            title={i18n.t("AssessmentMenu.removeAnswer")}
            onClick={props.onRemoveAnswer}
          >
            <LuMinus />
          </button>
          <span className="am-div" />
        </>
      )}
      {props.onFitScreen && (
        <button
          type="button"
          className="am-ic"
          aria-label={i18n.t("AssessmentMenu.fitToScreen")}
          title={i18n.t("AssessmentMenu.fitToScreen")}
          onClick={props.onFitScreen}
        >
          <LuMaximize />
        </button>
      )}
      {props.onDuplicate && (
        <button
          type="button"
          className="am-ic"
          aria-label={i18n.t("AssessmentMenu.duplicate")}
          title={i18n.t("AssessmentMenu.duplicate")}
          onClick={props.onDuplicate}
        >
          <LuCopy />
        </button>
      )}
      {props.onPreview && (
        <button
          type="button"
          className="am-ic"
          aria-label={i18n.t("AssessmentMenu.preview")}
          title={i18n.t("AssessmentMenu.preview")}
          onClick={props.onPreview}
        >
          <LuEye />
        </button>
      )}
      <span className="am-div" />
      <button
        type="button"
        className={"am-ic am-accent" + (open ? " am-open" : "")}
        aria-label={i18n.t("AssessmentMenu.moreOptions")}
        title={i18n.t("AssessmentMenu.moreOptions")}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <LuSettings2 />
      </button>
      <button
        type="button"
        className="am-ic am-danger"
        aria-label={i18n.t("AssessmentMenu.deleteQuestion")}
        title={i18n.t("AssessmentMenu.delete")}
        onClick={props.onBoxDeleted}
      >
        <LuTrash2 />
      </button>

      {open && (
        <div
          className="assessmentBoxMenu am-popover"
          role="group"
          aria-label={i18n.t("AssessmentMenu.questionOptions")}
          style={{ position: "absolute", top: 46, right: 0 }}
        >
          {menu.rows.map((row) => (
            <Row
              key={row.key}
              row={row}
              state={state}
              setKey={setKey}
              onAddAnswer={props.onAddAnswer}
              onRemoveAnswer={props.onRemoveAnswer}
              openSettings={props.openSettings}
              toolbarId={toolbar.id}
            />
          ))}
          {/* Points (weight) is its own row — every scored question can set how
              many points it is worth, independent of the partial-credit toggle.
              FreeResponse (açık uçlu) is excluded: it isn't auto-scored here. */}
          {props.onSetWeight && toolbar.pluginId !== "FreeResponse" && (
            <div className="am-row">
              <span className="am-lbl" id="am-num-weight">
                {i18n.t("AssessmentMenu.points")}
              </span>
              <input
                type="number"
                className="am-num"
                min={0}
                aria-labelledby="am-num-weight"
                value={props.weight == null ? "" : props.weight}
                onChange={(e) =>
                  props.onSetWeight(e.target.value === "" ? 0 : Number(e.target.value))
                }
              />
            </div>
          )}
          <div className="am-rowdiv" />
          {props.onPreview && (
            <div
              className="am-row am-clickable"
              role="button"
              tabIndex={0}
              onClick={props.onPreview}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  props.onPreview();
                }
              }}
            >
              <span className="am-lbl">
                <LuEye />
                {i18n.t("AssessmentMenu.previewThisQuestion")}
              </span>
              <LuChevronRight />
            </div>
          )}
          <div
            className="am-row am-del"
            role="button"
            tabIndex={0}
            onClick={props.onBoxDeleted}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                props.onBoxDeleted();
              }
            }}
          >
            <span className="am-lbl">
              <LuTrash2 />
              {i18n.t("AssessmentMenu.deleteQuestion")}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const portalRoot =
    document.getElementById("topPillRoot") || document.getElementById("editorBoxIconsRoot");
  return portalRoot ? ReactDOM.createPortal(pill, portalRoot) : pill;
}
